import React, { useState, useEffect } from 'react';
import API_URL from '../../../config';
import axios from 'axios';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Loader2
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import TarifaForm from './TarifaForm';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { AdminService } from '../../../application/services/AdminService';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_URL_TARIFAS = `${API_URL}/api/v2/tarifas`;

const TarifasPage = () => {
  const { isSuperAdmin, user, loading: authLoading } = useAuth();
  const [tarifas, setTarifas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [selectedTarifa, setSelectedTarifa] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const toast = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
        const promises = [AdminService.getAllTarifas()]; // Use AdminService for consistency

        if (isSuperAdmin()) {
            promises.push(AdminService.getEmpresas());
        }

        const [tarifasData, empresasData] = await Promise.all(promises);
        
        setTarifas(tarifasData || []);
        
        if (isSuperAdmin()) {
            const listaEmpresas = Array.isArray(empresasData) ? empresasData : (empresasData?.data || []);
            setEmpresas(listaEmpresas);
        }
    } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar datos');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (!authLoading) {
        fetchData();
    }
    // Dependency on user ensures that if user state updates (e.g. role loaded), we re-fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]); 

  const handleOpenCreate = () => {
      setSelectedTarifa(null);
      setShowModal(true);
  };

  const handleOpenEdit = (tarifa) => {
      setSelectedTarifa(tarifa);
      setShowModal(true);
  };

  const handleDelete = (tarifa) => {
      setSelectedTarifa(tarifa);
      setShowDeleteConfirm(true);
  };

  const handleSuccess = () => {
      setShowModal(false);
      fetchData();
  };

    const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
          headers: { Authorization: `Bearer ${token}` }
      };
      await axios.delete(`${API_URL_TARIFAS}/${selectedTarifa.id_tarifa}`, config);
      toast.success('Tarifa eliminada exitosamente');
      fetchData();
    } catch (error) {
      console.error('Error deleting tarifa:', error);
      toast.error('Error al eliminar tarifa');
    } finally {
        setShowDeleteConfirm(false);
        setSelectedTarifa(null);
    }
  };

  const filteredTarifas = tarifas.filter(t => {
      const term = searchTerm.toLowerCase();
      
      // Búsqueda por múltiples campos
      const matchId = t.id_tarifa?.toString().includes(term);
      const matchNombre = t.nombre_aplicacion?.toLowerCase().includes(term);
      const matchTipo = t.tipo_tarifa?.toLowerCase().includes(term);
      const matchValor = t.valor_base?.toString().includes(term);
      const matchEmpresa = isSuperAdmin() && (
          t.Empresa?.nombre?.toLowerCase().includes(term) || 
          t.id_empresa?.toString().includes(term)
      );

      return matchId || matchNombre || matchTipo || matchValor || matchEmpresa;
  });

  return (
    <DashboardLayout title="Gestión de Tarifas">
        <div className="space-y-6 animate-fade-in">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Tarifas</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Configura los costos y valores por consumo de agua.
                </p>
            </div>
            <Button onClick={handleOpenCreate} className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700">
                <Plus className="w-4 h-4" />
                Nueva Tarifa
            </Button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex items-center">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input 
                type="text" 
                placeholder="Buscar por nombre, tipo, empresa, ID, valor..."
                className="flex-1 outline-none text-gray-600 placeholder-gray-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                            <th className="px-4 py-3">ID</th>
                            {isSuperAdmin() && <th className="px-4 py-3">Empresa</th>}
                            <th className="px-4 py-3">Tipo</th>
                            <th className="px-4 py-3">Nombre</th>
                            <th className="px-4 py-3">Base ($)</th>
                            <th className="px-4 py-3">Base (m3)</th>
                            <th className="px-4 py-3">Valor Exceso ($)</th>
                            <th className="px-4 py-3">Reconexión ($)</th>
                            <th className="px-4 py-3">Mora ($)</th>
                            <th className="px-4 py-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            <tr>
                                <td colSpan={isSuperAdmin() ? 10 : 9} className="px-6 py-8 text-center text-gray-500">
                                    <div className="flex justify-center items-center">
                                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                                        Cargando tarifas...
                                    </div>
                                </td>
                            </tr>
                        ) : filteredTarifas.length === 0 ? (
                            <tr>
                                <td colSpan={isSuperAdmin() ? 10 : 9} className="px-6 py-8 text-center text-gray-500">
                                    No se encontraron tarifas
                                </td>
                            </tr>
                        ) : (
                            filteredTarifas.map((tarifa) => (
                                <tr key={tarifa.id_tarifa} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">#{tarifa.id_tarifa}</td>
                                    {isSuperAdmin() && (
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {tarifa.Empresa?.nombre || tarifa.id_empresa} 
                                        </td>
                                    )}
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                            tarifa.tipo_tarifa === 'Domestica' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {tarifa.tipo_tarifa}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{tarifa.nombre_aplicacion}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">${tarifa.valor_base}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{tarifa.tarifa_base} m³</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">${tarifa.tarifa_exceso}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">${tarifa.monto_reconexion}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">${tarifa.porcentaje_mora}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button 
                                                onClick={() => handleOpenEdit(tarifa)}
                                                className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                                title="Editar"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(tarifa)}
                                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>

            {showModal && (
                <div style={{display: 'none'}}></div>
            ) && null}
            {showModal && (
                <TarifaForm 
                    onClose={() => setShowModal(false)}
                    onSuccess={handleSuccess}
                    initialData={selectedTarifa}
                    empresas={empresas}
                    isSuperAdmin={typeof isSuperAdmin === 'function' ? isSuperAdmin() : isSuperAdmin}
                    currentUserEmpresaId={user?.id_empresa || user?.empresaId}
                />
            )}

        <Modal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            title="Eliminar Tarifa"
            size="sm"
            footer={
                <>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        className="bg-red-600 hover:bg-red-700" 
                        onClick={confirmDelete}
                    >
                        Eliminar
                    </Button>
                </>
            }
        >
            <div className="text-gray-600">
                <p>¿Estás seguro de que deseas eliminar la tarifa <strong>{selectedTarifa?.nombre_aplicacion}</strong>?</p>
                <p className="text-sm mt-2 text-red-500">Esta acción no se puede deshacer.</p>
            </div>
        </Modal>

        </div>
    </DashboardLayout>
  );
};
export default TarifasPage;
